import { injectable } from "@launchtray/tsyringe-async";
import { Vector3 } from "three";
import { GameView3d } from "../views/GameView3d";
import { Spawner } from "../components/3d/spawner/Spawner";
import { ISpawnable } from "../components/3d/spawner/ISpawnable";
import { MoleHole } from "../components/3d/gamefield/mole/MoleHole";
import { BaseMole } from "../components/3d/gamefield/mole/BaseMole";
import { Box } from "../components/3d/gamefield/box/Box";
import { InterfaceCheckUtils } from "../../../common/utils/InterfaceCheckUtils";
import { Event } from "event-system";
import { BoosterType } from "../../../common/models/data/BoosterType";
import { WeaponType } from "./data/WeaponType";
import { isMobile } from "pixi.js";
import { BoxReward } from "../components/3d/gamefield/box/BoxReward";

@injectable()
export class GameViewModel {
    public readonly spawnBooster: Event<BoosterType, number, number, BoxReward>;
    public readonly spawnCoin: Event<number, number, BoxReward>;
    public readonly spawnHealth: Event<number, number, BoxReward>;
    public readonly spawnWeapon: Event<WeaponType, number, number, BoxReward>;

    private static readonly INDEX_X_STEP = 0.65;
    private static readonly INDEX_Z_STEP = 2.0;
    private static readonly DESKTOP_X_GROW_MULTIPLIER = 0.5;
    private static readonly MOBILE_X_GROW_MULTIPLIER = 0.25;
    private static readonly DESKTOP_SPACING_MULTIPLIER = 1.75;
    private static readonly MOBILE_SPACING_MULTIPLIER = 1.75;
    private static readonly DESKTOP_GAME_FIELD_WIDTH: number = 10;
    private static readonly DESKTOP_GAME_FIELD_HEIGHT: number = 9;
    private static readonly MOBILE_GAME_FIELD_WIDTH: number = 4;
    private static readonly MOBILE_GAME_FIELD_HEIGHT: number = 9;
    public static get GAME_FIELD_WIDTH(): number {
        return isMobile.phone ? GameViewModel.MOBILE_GAME_FIELD_WIDTH : GameViewModel.DESKTOP_GAME_FIELD_WIDTH;
    }
    public static get GAME_FIELD_HEIGHT(): number {
        return isMobile.phone ? GameViewModel.MOBILE_GAME_FIELD_HEIGHT : GameViewModel.DESKTOP_GAME_FIELD_HEIGHT;
    }
    public static get SPACING_MULTIPLIER(): number {
        return isMobile.phone ? GameViewModel.MOBILE_SPACING_MULTIPLIER : GameViewModel.DESKTOP_SPACING_MULTIPLIER;
    }
    public static get X_GROW_MULTIPLIER(): number {
        return isMobile.phone ? GameViewModel.MOBILE_X_GROW_MULTIPLIER : GameViewModel.DESKTOP_X_GROW_MULTIPLIER;
    }
    private readonly xOffset: number;
    private readonly zOffset: number;

    private initialized: boolean;
    private spawners: ReadonlyArray<Spawner>;
    private spawnedItems: ReadonlySet<ISpawnable>;

    public constructor(
    ) {
        this.initialized = false;

        if (isMobile.phone) {
            this.xOffset = -(GameViewModel.GAME_FIELD_WIDTH - 1) * GameViewModel.INDEX_X_STEP * 0.5 - 0.5;
            this.zOffset = (GameViewModel.GAME_FIELD_HEIGHT - 1) * GameViewModel.INDEX_Z_STEP * 0.5 + GameViewModel.INDEX_Z_STEP - 4.5;
        } else {
            this.xOffset = -(GameViewModel.GAME_FIELD_WIDTH - 1) * GameViewModel.INDEX_X_STEP * 0.5 - 2;
            this.zOffset = (GameViewModel.GAME_FIELD_HEIGHT - 1) * GameViewModel.INDEX_Z_STEP * 0.5 + GameViewModel.INDEX_Z_STEP - 4;
        }

        this.spawnBooster = new Event<BoosterType, number, number, BoxReward>();
        this.spawnCoin = new Event<number, number, BoxReward>();
        this.spawnHealth = new Event<number, number, BoxReward>();
        this.spawnWeapon = new Event<WeaponType, number, number, BoxReward>();
    }

    public initialize(gameView: GameView3d): void {
        if (this.initialized) {
            throw new Error("You should not initialize GameViewModel twice.");
        }
        this.spawners = gameView.Spawners;
        this.spawnedItems = gameView.SpawnedItems;
    }

    public indexToGameCoords(index: number): Vector3 {
        const yIndex = Math.floor(index / GameViewModel.GAME_FIELD_WIDTH);
        const xIndex = index - yIndex * GameViewModel.GAME_FIELD_WIDTH;

        const lineXMultiplier =
            (yIndex * (xIndex - (GameViewModel.GAME_FIELD_WIDTH - 1) * 0.5)) /
            ((GameViewModel.GAME_FIELD_WIDTH - 1) * 0.5);

        const x =
            this.xOffset +
            (xIndex * GameViewModel.INDEX_X_STEP * GameViewModel.SPACING_MULTIPLIER) -
            (yIndex % 2 * GameViewModel.INDEX_X_STEP * 0.5 * GameViewModel.SPACING_MULTIPLIER) +
            lineXMultiplier * GameViewModel.X_GROW_MULTIPLIER * GameViewModel.SPACING_MULTIPLIER;

        const z = this.zOffset + yIndex * GameViewModel.INDEX_Z_STEP * GameViewModel.SPACING_MULTIPLIER;

        return new Vector3(x, 0, z);
    }

    public gameCoordsToIndex(coords: Vector3): number {
        const { x, z } = coords;

        // Compute Y index from Z
        const yIndex = Math.round(
            (z - this.zOffset) / (GameViewModel.INDEX_Z_STEP * GameViewModel.SPACING_MULTIPLIER)
        );

        const approxCenterXOffset = (GameViewModel.GAME_FIELD_WIDTH - 1) * 0.5;
        const lineXMultiplierDenominator = approxCenterXOffset;

        let bestMatch = { index: 0, distSq: Infinity };

        // Try each possible X index, pick the closest
        for (let xIndex = 0; xIndex < GameViewModel.GAME_FIELD_WIDTH; xIndex++) {
            const lineXMultiplier =
                (yIndex * (xIndex - approxCenterXOffset)) /
                lineXMultiplierDenominator;

            const candidateX =
                this.xOffset +
                (xIndex * GameViewModel.INDEX_X_STEP * GameViewModel.SPACING_MULTIPLIER) -
                (yIndex % 2) * (GameViewModel.INDEX_X_STEP * 0.5 * GameViewModel.SPACING_MULTIPLIER) +
                lineXMultiplier * GameViewModel.X_GROW_MULTIPLIER * GameViewModel.SPACING_MULTIPLIER;

            const dx = candidateX - x;
            const distSq = dx * dx;

            if (distSq < bestMatch.distSq) {
                bestMatch = { index: xIndex, distSq };
            }
        }

        const xIndex = bestMatch.index;
        return yIndex * GameViewModel.GAME_FIELD_WIDTH + xIndex;
    }

    public getNearestIndexToCoordinates(coordinates: Vector3, fieldIndexes: number[], indexToExclude?: number): number {
        const currentIndex = this.gameCoordsToIndex(coordinates);
        if (fieldIndexes.length === 0) return undefined;

        let nearest = fieldIndexes[0];
        let minDiff = Math.abs(nearest - currentIndex);

        for (let i = 1; i < fieldIndexes.length; i++) {
            const fieldIndex = fieldIndexes[i];
            if (fieldIndex == indexToExclude) {
                continue;
            }
            const diff = Math.abs(fieldIndex - currentIndex);
            if (diff < minDiff) {
                nearest = fieldIndex;
                minDiff = diff;
            }
        }

        return nearest;
    }

    public getFreeSpawnIndices(): Array<number> {
        const totalSize = GameViewModel.GAME_FIELD_WIDTH * GameViewModel.GAME_FIELD_HEIGHT;

        const occupied = new Set([
            ...this.spawners.map(s => s.fieldIndex),
            ...Array.from(this.spawnedItems, item => item.fieldIndex)
        ]);

        return Array.from({ length: totalSize }, (_, i) => i).filter(i => !occupied.has(i));
    }

    public get spawnedMoles(): Array<BaseMole> {
        return Array.from(this.spawnedItems).map<BaseMole>(item => {
            if (item instanceof BaseMole) {
                return item;
            }
            if (item instanceof MoleHole) {
                return item.mole;
            }
            return null;
        }).filter(item => item != null);
    }

    public get aliveSpawnedMoles(): Array<BaseMole> {
        return this.spawnedMoles.filter(item => item.alive);
    }

    public get aliveBoxes(): Array<Box> {
        return Array.from(this.spawnedItems).filter(InterfaceCheckUtils.isBox).filter(box => box.alive);
    }

    public getAliveMolesInRadius(center: Vector3, radius: number = 3): Array<BaseMole> {
        const result: Array<BaseMole> = [];
        for (const mole of this.aliveSpawnedMoles) {
            let moleWorldPosition = new Vector3();
            mole.getWorldPosition(moleWorldPosition);
            const distance = moleWorldPosition.distanceTo(center);
            if (distance <= radius) {
                result.push(mole);
            }
        }
        return result;
    }

    public getAvailableBoxesInRadius(center: Vector3, radius: number = 3): Array<Box> {
        const result: Array<Box> = [];
        for (const box of this.aliveBoxes) {
            let moleWorldPosition = new Vector3();
            box.getWorldPosition(moleWorldPosition);
            const distance = moleWorldPosition.distanceTo(center);
            if (distance <= radius) {
                result.push(box);
            }
        }
        return result;
    }

    public spawnCoinOnScreen(value: number, index: number, doubleReward: BoxReward) {
        this.spawnCoin.emit(value, index, doubleReward);
    }

    public spawnHealthOnScreen(value: number, index: number, doubleReward: BoxReward) {
        this.spawnHealth.emit(value, index, doubleReward);
    }

    public spawnBoosterOnScreen(type: BoosterType, count: number, index: number, doubleReward: BoxReward) {
        this.spawnBooster.emit(type, count, index, doubleReward);
    }

    public spawnWeaponOnScreen(type: WeaponType, ammo: number, index: number, doubleReward: BoxReward) {
        this.spawnWeapon.emit(type, ammo, index, doubleReward);
    }
}
